/**
 * ContentForge — Products View (Phase 15D)
 *
 * Products inventory with stock status overview, search, filtering by source
 * and stock status, and integration with CSV/Web sources.
 */

import { html } from "lit";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Client = any;

// ─── Types ──────────────────────────────────────────────────────────────────

type Product = {
  id: string;
  title: string;
  category: string;
  price: number;
  imageUrls: string;
  affCode: string;
  affiliateLink?: string;
  stockStatus: "in-stock" | "out-of-stock" | "unknown" | "error";
  csvSourceId: string;
  webSourceId?: string;
  description: string;
  lastCheckedAt: number;
};

type CsvSource = {
  id: string;
  siteId: string;
  filename: string;
  active: boolean;
};

// ─── State ──────────────────────────────────────────────────────────────────

let _products: Product[] = [];
let _sources: CsvSource[] = [];
let _loading = false;
let _search = "";
let _stockFilter: "all" | "in-stock" | "out-of-stock" | "unknown" = "all";
let _sourceFilter = "all";
let _page = 0;
const PAGE_SIZE = 50;
let _siteId = "";
let _debounceTimer: ReturnType<typeof setTimeout> | null = null;
let _rerender: (() => void) | null = null;

// ─── Data Loading ───────────────────────────────────────────────────────────

async function loadData(client: Client, activeSiteId: string | null) {
  if (!client) {
    return;
  }
  _loading = true;
  _rerender?.();

  try {
    // Use activeSiteId from the project switcher, or fetch first site
    if (activeSiteId) {
      _siteId = activeSiteId;
    } else if (!_siteId) {
      const sitesRes = await client.request("contentforge.sites.list", {});
      if (sitesRes?.sites?.length > 0) {
        _siteId = sitesRes.sites[0].id;
      } else {
        _loading = false;
        _rerender?.();
        return;
      }
    }

    // Load sources
    const srcRes = await client.request("contentforge.csv.list", {});
    _sources = srcRes?.csvSources ?? [];

    // Load products
    let products: Product[];
    if (_search.trim()) {
      const res = await client.request("contentforge.products.search", {
        siteId: _siteId,
        query: _search,
      });
      products = res?.products ?? [];
    } else {
      const params: Record<string, unknown> = {
        siteId: _siteId,
        activeOnly: true,
      };
      if (_sourceFilter !== "all") {
        params.csvSourceId = _sourceFilter;
      }
      const res = await client.request("contentforge.products.list", params);
      products = res?.products ?? [];
    }

    // Apply stock filter client-side
    if (_stockFilter !== "all") {
      products = products.filter((p) => p.stockStatus === _stockFilter);
    }

    _products = products;
    _page = 0;
  } catch (err) {
    console.error("[Products] Load failed:", err);
  }

  _loading = false;
  _rerender?.();
}

function debouncedLoad(client: Client, activeSiteId: string | null) {
  if (_debounceTimer) {
    clearTimeout(_debounceTimer);
  }
  _debounceTimer = setTimeout(() => void loadData(client, activeSiteId), 300);
}

// ─── Stats Computation ──────────────────────────────────────────────────────

function computeStats() {
  const total = _products.length;
  const inStock = _products.filter((p) => p.stockStatus === "in-stock").length;
  const outOfStock = _products.filter((p) => p.stockStatus === "out-of-stock").length;
  const unknown = _products.filter(
    (p) => p.stockStatus === "unknown" || p.stockStatus === "error",
  ).length;
  const avgPrice = total > 0 ? _products.reduce((s, p) => s + p.price, 0) / total : 0;
  return { total, inStock, outOfStock, unknown, avgPrice };
}

// ─── Render ─────────────────────────────────────────────────────────────────

export function renderCfProducts(
  opts: { client: Client; activeSiteId: string | null; onRefresh: () => void },
  rerender: () => void,
) {
  _rerender = rerender;

  // Reload when site changes or on first render
  const reqSiteId = opts.activeSiteId || _siteId;
  if ((!_loading && _products.length === 0) || (reqSiteId && reqSiteId !== _siteId)) {
    void loadData(opts.client, opts.activeSiteId);
  }

  const stats = computeStats();
  const paged = _products.slice(_page * PAGE_SIZE, (_page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(_products.length / PAGE_SIZE);

  return html`
    <div class="cf-products">
      <!-- Header -->
      <div class="cf-products__header">
        <h2 class="cf-products__title">📦 Products</h2>
        <button
          class="oc-btn oc-btn--sm"
          @click=${() => void loadData(opts.client, opts.activeSiteId)}
        >
          Refresh
        </button>
      </div>

      <!-- Stats Bar -->
      <div class="cf-products__stats">
        <div class="cf-products__stat">
          <span class="cf-products__stat-val">${stats.total}</span>
          <span class="cf-products__stat-lbl">Total</span>
        </div>
        <div class="cf-products__stat cf-products__stat--ok">
          <span class="cf-products__stat-val">${stats.inStock}</span>
          <span class="cf-products__stat-lbl">In Stock</span>
        </div>
        <div class="cf-products__stat cf-products__stat--error">
          <span class="cf-products__stat-val">${stats.outOfStock}</span>
          <span class="cf-products__stat-lbl">Out of Stock</span>
        </div>
        <div class="cf-products__stat">
          <span class="cf-products__stat-val">${stats.unknown}</span>
          <span class="cf-products__stat-lbl">Unknown</span>
        </div>
      </div>

      <!-- Filters -->
      <div class="cf-products__filters">
        <input
          class="cf-products__search"
          type="text"
          placeholder="Search products..."
          .value=${_search}
          @input=${(e: InputEvent) => {
            _search = (e.target as HTMLInputElement).value;
            debouncedLoad(opts.client, opts.activeSiteId);
          }}
        />
        <select
          class="cf-products__select"
          @change=${(e: Event) => {
            _sourceFilter = (e.target as HTMLSelectElement).value;
            void loadData(opts.client, opts.activeSiteId);
          }}
        >
          <option value="all" ?selected=${_sourceFilter === "all"}>All Sources</option>
          ${_sources.map(
            (s) => html`
              <option value=${s.id} ?selected=${_sourceFilter === s.id}>
                ${s.filename}
              </option>
            `,
          )}
        </select>
        <select
          class="cf-products__select"
          @change=${(e: Event) => {
            _stockFilter = (e.target as HTMLSelectElement).value as typeof _stockFilter;
            void loadData(opts.client, opts.activeSiteId);
          }}
        >
          <option value="all" ?selected=${_stockFilter === "all"}>All Status</option>
          <option value="in-stock" ?selected=${_stockFilter === "in-stock"}>✅ In Stock</option>
          <option value="out-of-stock" ?selected=${_stockFilter === "out-of-stock"}>❌ Out of Stock</option>
          <option value="unknown" ?selected=${_stockFilter === "unknown"}>❓ Unknown</option>
        </select>
        <span class="cf-products__count">${_products.length} products</span>
      </div>

      <!-- Loading -->
      ${
        _loading
          ? html`
              <div class="cf-products__loading">Loading products...</div>
            `
          : ""
      }

      <!-- Products Grid -->
      ${
        !_loading && _products.length === 0
          ? html`
              <div class="cf-products__empty">No products found</div>
            `
          : ""
      }

      ${
        !_loading && _products.length > 0
          ? html`
            <div class="cf-products__grid">
              ${paged.map((p) => renderProductCard(p))}
            </div>

            <!-- Pagination -->
            ${
              totalPages > 1
                ? html`
                  <div class="cf-products__pagination">
                    <button
                      class="oc-btn oc-btn--sm"
                      ?disabled=${_page === 0}
                      @click=${() => {
                        _page--;
                        _rerender?.();
                      }}
                    >
                      ← Prev
                    </button>
                    <span class="cf-products__page-info">
                      Page ${_page + 1} of ${totalPages}
                    </span>
                    <button
                      class="oc-btn oc-btn--sm"
                      ?disabled=${_page >= totalPages - 1}
                      @click=${() => {
                        _page++;
                        _rerender?.();
                      }}
                    >
                      Next →
                    </button>
                  </div>
                `
                : ""
            }
          `
          : ""
      }
    </div>
  `;
}

// ─── Product Card ───────────────────────────────────────────────────────────

function renderProductCard(p: Product) {
  const imgUrl = p.imageUrls?.split(",")[0]?.trim() || "";
  const stockIcon =
    p.stockStatus === "in-stock" ? "🟢" : p.stockStatus === "out-of-stock" ? "🔴" : "⚪";
  const stockLabel =
    p.stockStatus === "in-stock"
      ? "In Stock"
      : p.stockStatus === "out-of-stock"
        ? "Out of Stock"
        : "Unknown";
  const sourceName = _sources.find((s) => s.id === p.csvSourceId)?.filename || "—";
  const link = p.affiliateLink || p.affCode;

  return html`
    <div class="cf-products__card">
      <div class="cf-products__card-img-wrap">
        ${
          imgUrl
            ? html`<img
              class="cf-products__card-img"
              src=${imgUrl}
              alt=${p.title}
              loading="lazy"
              @error=${(e: Event) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />`
            : html`
                <div class="cf-products__card-no-img">📦</div>
              `
        }
      </div>
      <div class="cf-products__card-body">
        <div class="cf-products__card-title" title=${p.title}>
          ${p.title.length > 60 ? p.title.slice(0, 57) + "..." : p.title}
        </div>
        <div class="cf-products__card-meta">
          <span class="cf-products__card-price">
            ${p.price > 0 ? `${p.price.toFixed(2)} RON` : "—"}
          </span>
          <span class="cf-products__card-stock" title=${stockLabel}>
            ${stockIcon}
          </span>
        </div>
        <div class="cf-products__card-category">${p.category || "—"}</div>
        <div class="cf-products__card-source" title=${sourceName}>
          📁 ${sourceName.length > 20 ? sourceName.slice(0, 17) + "..." : sourceName}
        </div>
        ${
          link
            ? html`<a
              class="cf-products__card-link"
              href=${link}
              target="_blank"
              rel="noopener"
              >View →</a
            >`
            : ""
        }
      </div>
    </div>
  `;
}
